/**
 * Serviço de Monitores
 *
 * Gerencia operações CRUD de monitores usando PostgREST.
 * Tabela: monitor_profiles
 */

import { postgrestClient } from "../lib/postgrest/client";
import { postgrestClient as apiClient } from "../config/api";
import { registrarFalha } from "../lib/telemetria";

/**
 * Serviço de monitores
 */
export const monitoresService = {
  /**
   * Lista monitores com filtros
   * Para lista completa sem filtros, usa cache Redis via /v1/monitores (4h TTL)
   * Para queries filtradas ou com busca, usa PostgREST direto
   * @param {Object} options
   * @param {Object} options.filters - Filtros (ex: { ativo: "eq.true" })
   * @param {string} options.order - Ordenação (ex: "nome.asc")
   * @param {number} options.limit - Limite de resultados
   * @param {number} options.offset - Offset para paginação
   * @param {string} options.search - Busca por nome, email ou CPF
   * @param {boolean} options.includeCount - Se true, retorna { data, total }
   * @returns {Promise<Array|{data: Array, total: number}>}
   */
  async list(options = {}) {
    const { filters = {}, order, limit, offset, search, ativo, includeCount = false } = options;

    // Se não há filtros, busca, ou filtro de ativo, usa cache com paginação
    const hasFilters = Object.keys(filters).length > 0;
    const hasSearch = search && search.trim() !== "";
    const useCache = !hasFilters && !hasSearch && ativo === null;

    if (useCache) {
      try {
        // Construir query string para paginação
        const params = new URLSearchParams();
        if (limit) params.append("limit", limit);
        if (offset !== undefined) params.append("offset", offset);
        if (order && order !== "nome.asc") params.append("order", order);

        const url = `/v1/monitores${params.toString() ? "?" + params.toString() : ""}`;
        const cached = await apiClient.request(url, { method: "GET" });

        // Endpoint retorna { data, count, total, offset, limit, has_more }
        if (includeCount) {
          return {
            data: Array.isArray(cached?.data) ? cached.data : [],
            total: cached?.total || 0,
          };
        }
        return Array.isArray(cached?.data) ? cached.data : [];
      } catch (error) {
        registrarFalha("monitores.cache", error);
        // Fallback para PostgREST se cache falhar
      }
    }

    // Se houver busca, não podemos usar filtros em tabelas relacionadas diretamente
    // O PostgREST pode não suportar filtros em joins dessa forma
    // Vamos buscar todos e filtrar no frontend, ou usar uma abordagem diferente
    // Por enquanto, vamos buscar todos os monitores e fazer a busca no frontend
    // NOTA: Para melhor performance, seria ideal criar uma view ou RPC function

    // Filtrar por status ativo do usuário relacionado
    // PostgREST permite filtrar em tabelas relacionadas usando notação de ponto
    // Mas pode não funcionar corretamente, então vamos filtrar no frontend também
    if (ativo !== null && ativo !== undefined) {
      // Tenta filtrar no backend, mas se não funcionar, filtra no frontend
      filters["users.ativo"] = `eq.${ativo}`;
    }

    // Se houver busca, buscar todos os resultados sem paginação do backend
    // para poder paginar corretamente no frontend
    const shouldPaginateBackend = !hasSearch && ativo === undefined; // Só paginar no backend se não houver busca nem filtro de ativo

    // PostgREST não suporta ordenação direta em tabelas relacionadas
    // Não passar order aqui, ordenar no frontend após receber os dados
    // Ou criar uma view/RPC function que já faça o join e permita ordenação
    const result = await postgrestClient.get("monitor_profiles", {
      filters,
      limit: shouldPaginateBackend ? limit : undefined, // Sem limit se houver busca ou filtro complexo
      offset: shouldPaginateBackend ? offset : undefined, // Sem offset se houver busca ou filtro complexo
      includeCount,
      // Inclui dados do usuário relacionado usando a foreign key
      select: "*,users(*)",
    });

    // Extrai data e total se includeCount for true
    let data = result;
    let total = null;
    if (includeCount && result && typeof result === 'object' && 'data' in result) {
      data = result.data;
      total = result.total;
    }

    // Ordenar no frontend se necessário
    // Por padrão, ordenar por nome do usuário
    if (order && order.includes("nome")) {
      const dataArray = Array.isArray(data) ? data : data ? [data] : [];
      const sorted = dataArray.sort((a, b) => {
        const nomeA = (a.users?.nome || a.nome || "").toLowerCase();
        const nomeB = (b.users?.nome || b.nome || "").toLowerCase();
        return order.includes("desc") ? nomeB.localeCompare(nomeA) : nomeA.localeCompare(nomeB);
      });
      data = sorted;
    }

    // Retorna { data, total } se includeCount, senão retorna array
    if (includeCount) {
      return { data: Array.isArray(data) ? data : data ? [data] : [], total: total !== null ? total : (Array.isArray(data) ? data.length : 1) };
    }

    return data;
  },

  /**
   * Busca monitor por ID
   * @param {string|number} id - ID do monitor (pode ser monitor_profiles.id ou usuario_id)
   * @returns {Promise<Object>}
   */
  async getById(id) {
    const result = await postgrestClient.get("monitor_profiles", {
      filters: { id: `eq.${id}` },
      select: "*,users(*)",
    });
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Normaliza resposta do monitor para garantir estrutura consistente
   * @param {Object} data - Dados brutos do monitor
   * @returns {Object} - Dados normalizados
   */
  normalizeMonitorResponse(data) {
    if (!data) return null;

    // PostgREST pode retornar users como objeto ou array (quando há múltiplos relacionamentos)
    // Normalmente é um objeto quando há join 1:1
    // Se data.users não existir mas data tiver nome/email, então data já é o user
    const usersData = data.users
      ? (Array.isArray(data.users) ? data.users[0] : data.users)
      : (data.nome || data.email ? data : null);

    return {
      ...data,
      users: usersData,
      // Garantir que o nome esteja acessível diretamente
      nome: usersData?.nome || data.nome || "Monitor sem nome",
      // Incluir role para permitir separação entre monitores e gestores
      role: usersData?.role || data.role || "MONITOR",
    };
  },

  /**
   * Busca monitor por usuario_id
   * @param {number} usuarioId
   * @returns {Promise<Object>}
   */
  async getByUsuarioId(usuarioId) {
    // Primeiro tenta buscar em monitor_profiles
    let result = await postgrestClient.get("monitor_profiles", {
      filters: { usuario_id: `eq.${usuarioId}` },
      select: "*,users(*)",
    });
    let rawData = Array.isArray(result) ? (result.length > 0 ? result[0] : null) : result;

    // Se não encontrou em monitor_profiles, busca diretamente em users
    // Isso pode acontecer se o monitor foi criado mas não tem perfil ainda
    if (!rawData) {
      try {
        const userResult = await postgrestClient.get("users", {
          filters: {
            id: `eq.${usuarioId}`,
            role: "eq.MONITOR"
          },
        });
        const userData = Array.isArray(userResult) ? (userResult.length > 0 ? userResult[0] : null) : userResult;

        if (userData) {
          // Criar estrutura compatível com monitor_profiles
          rawData = {
            id: null, // Não tem perfil ainda
            usuario_id: userData.id,
            users: userData, // Incluir dados do usuário
            nome: userData.nome, // Para compatibilidade
          };
        }
      } catch (err) {
        console.warn(`[monitoresService.getByUsuarioId] Erro ao buscar usuário ${usuarioId} diretamente:`, err);
      }
    }

    return this.normalizeMonitorResponse(rawData);
  },

  /**
   * Busca monitores por vários usuario_ids em uma única chamada (batch)
   * @param {Array<number|string>} usuarioIds - Array de IDs de usuário
   * @returns {Promise<Map<string, string>>} Mapa de idStr -> nome do monitor
   */
  async getByUsuarioIds(usuarioIds) {
    const ids = Array.isArray(usuarioIds) ? usuarioIds : [usuarioIds];
    const uniqueIds = [...new Set(ids)].filter(id => id != null && id !== "");
    if (uniqueIds.length === 0) return new Map();

    try {
      const result = await postgrestClient.get("monitor_profiles", {
        filters: { usuario_id: `in.(${uniqueIds.join(",")})` },
        select: "usuario_id,*,users(nome)",
      });
      const arr = Array.isArray(result) ? result : result ? [result] : [];
      const mapa = new Map();
      arr.forEach((item) => {
        const id = item.usuario_id ?? item.id;
        if (id != null) {
          const usersData = Array.isArray(item.users) ? item.users[0] : item.users;
          const nome = usersData?.nome || item.nome || "Monitor sem nome";
          mapa.set(String(id), nome);
          if (String(id) !== String(Number(id))) {
            mapa.set(String(Number(id)), nome);
          }
        }
      });

      // Para IDs não encontrados em monitor_profiles, buscar em users
      const naoEncontrados = uniqueIds.filter((id) => !mapa.has(String(id)));
      if (naoEncontrados.length > 0) {
        const userResult = await postgrestClient.get("users", {
          filters: { id: `in.(${naoEncontrados.join(",")})`, role: "eq.MONITOR" },
          select: "id,nome",
        });
        const usersArr = Array.isArray(userResult) ? userResult : userResult ? [userResult] : [];
        usersArr.forEach((u) => {
          if (u?.id != null) {
            const nome = u.nome || "Monitor sem nome";
            mapa.set(String(u.id), nome);
          }
        });
      }
      return mapa;
    } catch (err) {
      console.warn("[monitoresService.getByUsuarioIds] Erro:", err);
      return new Map();
    }
  },

  /**
   * Cria novo monitor
   * @param {Object} monitorData
   * @returns {Promise<Object>}
   */
  async create(monitorData) {
    // Separar campos que pertencem a users dos que pertencem a monitor_profiles
    const usersFields = ['cpf', 'nome', 'password', 'role', 'ativo'];
    // Campos válidos da tabela monitor_profiles (excluindo id, usuario_id, created_at, updated_at que são gerenciados automaticamente)
    // tipo_instituicao foi removido do formulário
    const validProfileFields = [
      'cpf', 'data_nasc', 'sexo', 'cor_raca', 'uf_nascimento', 'municipio_nascimento',
      'tem_deficiencia', 'tipo_deficiencia', 'area_atuante', 'cep', 'zona_residencia',
      'nivel_escolaridade', 'tipo_ensino_medio', 'curso_formacao', 'nivel_grau',
      'ano_conclusao', 'instituicao_ensino', 'telefone',
      'endereco', 'area_pedagogica', 'area_esportiva'
    ];
    const userData = {};
    const profileData = {};

    Object.keys(monitorData).forEach(key => {
      if (usersFields.includes(key)) {
        userData[key] = monitorData[key];
      } else if (validProfileFields.includes(key)) {
        // Valida campos com constraints CHECK antes de adicionar
        let value = monitorData[key];

        // tipo_instituicao foi removido do formulário - ignorar se presente
        if (key === 'tipo_instituicao') {
          // Ignora o campo completamente
        }
        // Valida sexo - apenas 'MASCULINO' e 'FEMININO' são aceitos
        else if (key === 'sexo' && value !== undefined && value !== null && value !== "") {
          const validValues = ['MASCULINO', 'FEMININO'];
          const trimmedValue = String(value).trim();
          if (validValues.includes(trimmedValue)) {
            profileData[key] = trimmedValue;
          }
          // Se não for válido, não adiciona (ignora)
        }
        // Valida zona_residencia - apenas 'URBANA' e 'RURAL' são aceitos
        else if (key === 'zona_residencia' && value !== undefined && value !== null && value !== "") {
          const validValues = ['URBANA', 'RURAL'];
          const trimmedValue = String(value).trim();
          if (validValues.includes(trimmedValue)) {
            profileData[key] = trimmedValue;
          }
          // Se não for válido, não adiciona (ignora)
        }
        // Para outros campos válidos, adiciona normalmente (mas remove se vazio)
        else if (value !== undefined && value !== null && value !== "") {
          profileData[key] = value;
        }
        // Campos vazios, null ou undefined são ignorados
      }
      // Campos inválidos são ignorados
    });

    // Primeiro criar o usuário na tabela users
    if (!userData.cpf || !userData.nome || !userData.password) {
      throw new Error("Dados de usuário são obrigatórios (cpf, nome, password)");
    }

    // Definir role como MONITOR por padrão se não foi especificado
    if (!userData.role) {
      userData.role = 'MONITOR';
    }

    // Definir ativo como true por padrão se não foi especificado
    if (userData.ativo === undefined) {
      userData.ativo = true;
    }

    // Usar o usersService que faz hash da senha corretamente
    const { usersService } = await import("./users.service");
    const newUser = await usersService.create(userData);
    const userId = newUser.id;

    if (!userId) {
      throw new Error("Erro ao criar usuário");
    }

    // O banco pode criar monitor_profiles automaticamente via trigger
    // (migration 154). Para evitar erro de UNIQUE (usuario_id), primeiro
    // tenta reaproveitar o perfil já criado e apenas complementar os campos.
    const existingProfile = await postgrestClient.get("monitor_profiles", {
      filters: { usuario_id: `eq.${userId}` },
      select: "id,usuario_id",
    });
    const existingProfileData = Array.isArray(existingProfile)
      ? existingProfile[0]
      : existingProfile;

    if (existingProfileData?.id) {
      if (Object.keys(profileData).length > 0) {
        await postgrestClient.patch(
          "monitor_profiles",
          { id: `eq.${existingProfileData.id}` },
          profileData
        );
      }
      return await this.getById(existingProfileData.id);
    }

    // Fallback para ambientes sem trigger de auto criação de perfil
    profileData.usuario_id = userId;
    const result = await postgrestClient.post("monitor_profiles", profileData);
    const createdProfile = Array.isArray(result) ? result[0] : result;
    return await this.getById(createdProfile?.id || createdProfile);
  },

  /**
   * Atualiza monitor
   * @param {string|number} id - ID do monitor_profiles
   * @param {Object} monitorData
   * @returns {Promise<Object>}
   */
  async update(id, monitorData) {
    // Primeiro, obter o monitor para pegar o usuario_id
    const monitor = await this.getById(id);
    if (!monitor || !monitor.usuario_id) {
      throw new Error("Monitor não encontrado ou sem usuario_id");
    }

    // Separar campos que pertencem a users dos que pertencem a monitor_profiles
    const usersFields = ['cpf', 'nome', 'password', 'role', 'ativo'];
    // Campos válidos da tabela monitor_profiles (excluindo id, usuario_id, created_at, updated_at que são gerenciados automaticamente)
    // tipo_instituicao foi removido do formulário
    const validProfileFields = [
      'cpf', 'data_nasc', 'sexo', 'cor_raca', 'uf_nascimento', 'municipio_nascimento',
      'tem_deficiencia', 'tipo_deficiencia', 'area_atuante', 'cep', 'zona_residencia',
      'nivel_escolaridade', 'tipo_ensino_medio', 'curso_formacao', 'nivel_grau',
      'ano_conclusao', 'instituicao_ensino', 'telefone',
      'endereco', 'area_pedagogica', 'area_esportiva'
    ];
    const userData = {};
    const profileData = {};

    Object.keys(monitorData).forEach(key => {
      if (usersFields.includes(key)) {
        userData[key] = monitorData[key];
      } else if (validProfileFields.includes(key)) {
        // Valida campos com constraints CHECK antes de adicionar
        let value = monitorData[key];

        // tipo_instituicao foi removido do formulário - ignorar se presente
        if (key === 'tipo_instituicao') {
          // Ignora o campo completamente
        }
        // Valida sexo - apenas 'MASCULINO' e 'FEMININO' são aceitos
        else if (key === 'sexo' && value !== undefined && value !== null && value !== "") {
          const validValues = ['MASCULINO', 'FEMININO'];
          const trimmedValue = String(value).trim();
          if (validValues.includes(trimmedValue)) {
            profileData[key] = trimmedValue;
          }
          // Se não for válido, não adiciona (ignora)
        }
        // Valida zona_residencia - apenas 'URBANA' e 'RURAL' são aceitos
        else if (key === 'zona_residencia' && value !== undefined && value !== null && value !== "") {
          const validValues = ['URBANA', 'RURAL'];
          const trimmedValue = String(value).trim();
          if (validValues.includes(trimmedValue)) {
            profileData[key] = trimmedValue;
          }
          // Se não for válido, não adiciona (ignora)
        }
        // Para outros campos válidos, adiciona normalmente (mas remove se vazio)
        else if (value !== undefined && value !== null && value !== "") {
          profileData[key] = value;
        }
        // Campos vazios, null ou undefined são ignorados
      }
      // Campos inválidos são ignorados
    });

    // Atualizar tabela users se houver campos de usuário
    if (Object.keys(userData).length > 0) {
      // Se houver password, usar a API que faz hash corretamente
      if (userData.password) {
        const { usersService } = await import("./users.service");
        await usersService.update(monitor.usuario_id, userData);
      } else {
        // Atualizar campos de users via PostgREST (sem password)
        await postgrestClient.patch("users", { id: monitor.usuario_id }, userData);
      }
    }

    // Atualizar tabela monitor_profiles
    if (Object.keys(profileData).length > 0) {
      await postgrestClient.patch("monitor_profiles", { id }, profileData);
    }

    // Retornar o monitor atualizado
    return await this.getById(id);
  },

  /**
   * Cria ou atualiza o perfil de um monitor que já existe como usuário mas não tem monitor_profiles
   * @param {number} usuarioId - ID do usuário existente em users
   * @param {Object} monitorData - Dados do monitor (sem cpf/password obrigatórios)
   * @returns {Promise<Object>}
   */
  async upsertProfileByUsuarioId(usuarioId, monitorData) {
    const usersFields = ['nome', 'password', 'role', 'ativo'];
    const validProfileFields = [
      'cpf', 'data_nasc', 'sexo', 'cor_raca', 'uf_nascimento', 'municipio_nascimento',
      'tem_deficiencia', 'tipo_deficiencia', 'area_atuante', 'cep', 'zona_residencia',
      'nivel_escolaridade', 'tipo_ensino_medio', 'curso_formacao', 'nivel_grau',
      'ano_conclusao', 'instituicao_ensino', 'telefone',
      'endereco', 'area_pedagogica', 'area_esportiva'
    ];

    const userData = {};
    const profileData = {};

    Object.keys(monitorData).forEach(key => {
      if (usersFields.includes(key)) {
        userData[key] = monitorData[key];
      } else if (validProfileFields.includes(key)) {
        const value = monitorData[key];
        if (key === 'sexo' && value) {
          const valid = ['MASCULINO', 'FEMININO'];
          const trimmed = String(value).trim();
          if (valid.includes(trimmed)) profileData[key] = trimmed;
        } else if (key === 'zona_residencia' && value) {
          const valid = ['URBANA', 'RURAL'];
          const trimmed = String(value).trim();
          if (valid.includes(trimmed)) profileData[key] = trimmed;
        } else if (value !== undefined && value !== null && value !== '') {
          profileData[key] = value;
        }
      }
    });

    // Atualizar campos do usuário se houver
    if (Object.keys(userData).length > 0) {
      if (userData.password) {
        const { usersService } = await import("./users.service");
        await usersService.update(usuarioId, userData);
      } else {
        await postgrestClient.patch("users", { id: usuarioId }, userData);
      }
    }

    // Verificar se já existe um perfil para este usuário
    const existingProfile = await postgrestClient.get("monitor_profiles", {
      filters: { usuario_id: `eq.${usuarioId}` },
      select: "id,usuario_id",
    });
    const existing = Array.isArray(existingProfile) ? existingProfile[0] : existingProfile;

    if (existing?.id) {
      if (Object.keys(profileData).length > 0) {
        await postgrestClient.patch("monitor_profiles", { id: `eq.${existing.id}` }, profileData);
      }
      return await this.getById(existing.id);
    }

    // Criar novo perfil
    profileData.usuario_id = usuarioId;
    const result = await postgrestClient.post("monitor_profiles", profileData);
    const created = Array.isArray(result) ? result[0] : result;
    return await this.getById(created?.id || created);
  },

  /**
   * Remove monitor (soft delete via ativo=false ou hard delete)
   * @param {string|number} id
   * @returns {Promise<null>}
   */
  async delete(id) {
    return postgrestClient.delete("monitor_profiles", { id });
  },

  /**
   * Busca monitores de um núcleo
   * @param {string} nucleoId
   * @param {Object} options - Opções adicionais
   * @returns {Promise<Array>}
   */
  async getByNucleo(nucleoId, options = {}) {
    // Nova estratégia: consulta única via view (sem N+1 por usuário).
    const filters = { nucleo_id: `eq.${nucleoId}` };
    if (options.ativo !== null && options.ativo !== undefined) {
      filters.vinculo_ativo = `eq.${options.ativo}`;
    }

    const { ativo, ...optionsSemAtivo } = options;
    const rows = await postgrestClient.get("vw_monitores_nucleo", {
      filters,
      order: "nome.asc",
      ...optionsSemAtivo,
    });

    const arr = Array.isArray(rows) ? rows : rows ? [rows] : [];
    return arr.map(row => ({
      id: row.monitor_profile_id || null,
      usuario_id: row.usuario_id,
      cpf: row.monitor_cpf || row.usuario_cpf || null,
      area_atuante: row.area_atuante || null,
      nome: row.nome || "Monitor sem nome",
      email: row.email || "",
      role: row.role || "MONITOR",
      ativo: row.usuario_ativo !== false,
      users: {
        id: row.usuario_id,
        nome: row.nome || "Monitor sem nome",
        email: row.email || "",
        role: row.role || "MONITOR",
        ativo: row.usuario_ativo !== false,
        cpf: row.usuario_cpf || null,
      },
      vinculo: {
        id: row.vinculo_id,
        data_inicio: row.data_inicio,
        data_fim: row.data_fim,
      },
    }));
  },

  /**
   * Vincula monitor a um núcleo
   * @param {string|number} monitorId - ID do monitor_profiles
   * @param {Object} nucleoData - { nucleo_id, data_inicio, etc }
   * @returns {Promise<Object>}
   */
  async vincularNucleo(monitorId, nucleoData) {
    // Primeiro, obter o usuario_id do monitor_profiles
    const monitor = await this.getById(monitorId);
    if (!monitor || !monitor.usuario_id) {
      throw new Error("Monitor não encontrado ou sem usuario_id");
    }

    const payload = {
      usuario_id: monitor.usuario_id,
      ...nucleoData,
    };
    const result = await postgrestClient.post("usuario_nucleo", payload);
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Desvincula monitor de um núcleo
   * @param {string|number} monitorId - ID do monitor_profiles
   * @param {string|number} relacionamentoId - ID do relacionamento (usuario_nucleo.id)
   * @returns {Promise<null>}
   */
  async desvincularNucleo(monitorId, relacionamentoId) {
    return postgrestClient.delete("usuario_nucleo", { id: relacionamentoId });
  },


  /**
   * Lista núcleos de um monitor
   * @param {string|number} monitorId - ID do monitor_profiles
   * @param {Object} options - { ativo: boolean }
   * @returns {Promise<Array>}
   */
  async getNucleos(monitorId, options = {}) {
    // Primeiro, obter o usuario_id do monitor_profiles
    const monitor = await this.getById(monitorId);
    if (!monitor || !monitor.usuario_id) {
      console.warn(`[monitoresService.getNucleos] Monitor não encontrado ou sem usuario_id:`, monitorId);
      return [];
    }

    const filters = { usuario_id: `eq.${monitor.usuario_id}` };

    // Se ativo for especificado, filtrar por data_fim (null = ativo)
    if (options.ativo !== null && options.ativo !== undefined) {
      if (options.ativo) {
        filters.data_fim = "is.null";
      } else {
        filters.data_fim = "not.is.null";
      }
    }



    // Buscar vínculos sem tentar fazer join (PostgREST não reconhece a relação)
    const vinculos = await postgrestClient.get("usuario_nucleo", {
      filters,
      select: "*",
    });



    if (!Array.isArray(vinculos) || vinculos.length === 0) {
      return [];
    }

    // Filtrar vínculos ativos se solicitado (garantir que data_fim seja null)
    let vinculosFiltrados = vinculos;
    if (options.ativo === true) {
      vinculosFiltrados = vinculos.filter(v => !v.data_fim);

    }

    // Buscar detalhes dos núcleos separadamente
    const { nucleosService } = await import("./nucleos.service");
    const nucleosComDetalhes = [];

    for (const vinculo of vinculosFiltrados) {
      try {
        const nucleo = await nucleosService.getById(vinculo.nucleo_id);
        if (nucleo) {
          // Retorna o vínculo com os detalhes do núcleo
          nucleosComDetalhes.push({
            id: vinculo.id,
            nucleo_id: vinculo.nucleo_id,
            usuario_id: vinculo.usuario_id,
            data_inicio: vinculo.data_inicio,
            data_fim: vinculo.data_fim,
            criado_em: vinculo.criado_em,
            // Adiciona os detalhes do núcleo
            nucleo: nucleo,
          });
        }
      } catch (err) {
        // Se não conseguir buscar o núcleo, ainda retorna o vínculo sem detalhes
        console.warn(`[monitoresService.getNucleos] Erro ao buscar detalhes do núcleo ${vinculo.nucleo_id}:`, err);
        nucleosComDetalhes.push({
          id: vinculo.id,
          nucleo_id: vinculo.nucleo_id,
          usuario_id: vinculo.usuario_id,
          data_inicio: vinculo.data_inicio,
          data_fim: vinculo.data_fim,
          criado_em: vinculo.criado_em,
          nucleo: null,
        });
      }
    }


    return nucleosComDetalhes;
  },
};

export default monitoresService;

